import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { SignP10Request, SignRequest } from '../models/certificate-info.model';
import { SignResponse } from '../models/sign-response.model';
import { SignCadesApiRequest, SignCadesApiResponse } from '../models/sign-cades-api.model';
import { SignPfxCadesApiRequest, SignPfxCadesApiResponse } from '../models/sign-pfx-cades-api.model';
import { SignPfxPdfApiRequest, SignPfxPdfApiResponse } from '../models/sign-pfx-pdf-api.model';
import { SignPdfApiRequest, SignPdfApiResponse } from '../models/sign-pdf-api.model';
import {
  SignCadesPfxMultipartParams,
  SignCadesTvMultipartParams,
  SignMultipartResult,
  SignPdfPfxMultipartParams,
  SignPdfTvMultipartParams
} from '../models/sign-multipart-api.model';
import { environment } from '../../environments/environment';
import { CipherService } from '../core/services/cipher.service';

@Injectable({
  providedIn: 'root'
})
export class SignService {
  private signUrl = `${environment.apiBaseUrl}/sign`;
  private signP10Url = `${environment.apiBaseUrl}/p10/sign`;
  private signPdfApiUrl = (environment as { signPdfApiUrl?: string }).signPdfApiUrl ?? `${environment.apiBaseUrl}/api/sign/pdf`;
  private signPdfPfxApiUrl = (environment as { signPdfPfxApiUrl?: string }).signPdfPfxApiUrl ?? `${environment.apiBaseUrl}/api/sign/pdf/pfx`;
  private signCadesApiUrl = (environment as { signCadesApiUrl?: string }).signCadesApiUrl ?? `${environment.apiBaseUrl}/api/sign/cades`;
  private signCadesPfxApiUrl = (environment as { signCadesPfxApiUrl?: string }).signCadesPfxApiUrl ?? `${environment.apiBaseUrl}/api/sign/cades/pfx`;

  private signPdfMultipartUrl =
    (environment as { signPdfMultipartApiUrl?: string }).signPdfMultipartApiUrl
    ?? `${this.signPdfApiUrl}/multipart`;
  private signPdfPfxMultipartUrl =
    (environment as { signPdfPfxMultipartApiUrl?: string }).signPdfPfxMultipartApiUrl
    ?? `${this.signPdfPfxApiUrl}/multipart`;
  private signCadesMultipartUrl =
    (environment as { signCadesMultipartApiUrl?: string }).signCadesMultipartApiUrl
    ?? `${this.signCadesApiUrl}/multipart`;
  private signCadesPfxMultipartUrl =
    (environment as { signCadesPfxMultipartApiUrl?: string }).signCadesPfxMultipartApiUrl
    ?? `${this.signCadesPfxApiUrl}/multipart`;

  constructor(
    private http: HttpClient,
    private cipher: CipherService
  ) {}

  /**
   * Firma PDF con Token Virtual - JSON Base64 (legacy) POST /api/sign/pdf.
   */
  signPdfTokenVirtual(request: SignPdfApiRequest): Observable<SignPdfApiResponse> {
    return this.postEncrypted<SignPdfApiResponse>(
      this.signPdfApiUrl,
      this.withOriginChannel(request)
    );
  }

  /** Firma PDF Token Virtual - multipart binario POST /api/sign/pdf/multipart. */
  signPdfTokenVirtualMultipart(params: SignPdfTvMultipartParams): Observable<SignMultipartResult> {
    return from(this.buildPdfTvForm(params)).pipe(
      switchMap((form) => this.postMultipartSign(this.signPdfMultipartUrl, form))
    );
  }

  /**
   * Firma PDF con certificado PFX/P12 (P10) - JSON Base64 POST /api/sign/pdf/pfx.
   */
  signPdfPfx(request: SignPfxPdfApiRequest): Observable<SignPfxPdfApiResponse> {
    return this.postEncrypted<SignPfxPdfApiResponse>(
      this.signPdfPfxApiUrl,
      this.withOriginChannel(request)
    );
  }

  /** Firma PDF P10 - multipart binario POST /api/sign/pdf/pfx/multipart. */
  signPdfPfxMultipart(params: SignPdfPfxMultipartParams): Observable<SignMultipartResult> {
    return from(this.buildPdfPfxForm(params)).pipe(
      switchMap((form) => this.postMultipartSign(this.signPdfPfxMultipartUrl, form))
    );
  }

  /**
   * Firma CAdES con Token Virtual - JSON Base64 POST /api/sign/cades.
   */
  signCadesTokenVirtual(request: SignCadesApiRequest): Observable<SignCadesApiResponse> {
    return this.postEncrypted<SignCadesApiResponse>(
      this.signCadesApiUrl,
      this.withOriginChannel(request)
    );
  }

  /** Firma CAdES Token Virtual - multipart binario POST /api/sign/cades/multipart. */
  signCadesTokenVirtualMultipart(params: SignCadesTvMultipartParams): Observable<SignMultipartResult> {
    return from(this.buildCadesTvForm(params)).pipe(
      switchMap((form) => this.postMultipartSign(this.signCadesMultipartUrl, form))
    );
  }

  /**
   * Firma CAdES con certificado PFX/P12 (P10) - JSON Base64 POST /api/sign/cades/pfx.
   */
  signCadesPfx(request: SignPfxCadesApiRequest): Observable<SignPfxCadesApiResponse> {
    return this.postEncrypted<SignPfxCadesApiResponse>(
      this.signCadesPfxApiUrl,
      this.withOriginChannel(request)
    );
  }

  /** Firma CAdES P10 - multipart binario POST /api/sign/cades/pfx/multipart. */
  signCadesPfxMultipart(params: SignCadesPfxMultipartParams): Observable<SignMultipartResult> {
    return from(this.buildCadesPfxForm(params)).pipe(
      switchMap((form) => this.postMultipartSign(this.signCadesPfxMultipartUrl, form))
    );
  }

  signPdf(signRequest: SignRequest): Observable<SignResponse> {
    return this.http.post<SignResponse>(`${this.signUrl}/pdf`, signRequest);
  }

  signCades(signRequest: SignRequest): Observable<SignResponse> {
    return this.http.post<SignResponse>(`${this.signUrl}/cades`, signRequest);
  }

  signP10Pdf(signRequest: SignP10Request): Observable<SignResponse> {
    return this.http.post<SignResponse>(`${this.signP10Url}/pdf`, signRequest);
  }

  signP10Cades(signRequest: SignP10Request): Observable<SignResponse> {
    return this.http.post<SignResponse>(`${this.signP10Url}/cades`, signRequest);
  }

  async buildPdfTvForm(params: SignPdfTvMultipartParams): Promise<FormData> {
    const form = new FormData();
    form.append('document', params.document, params.fileName || 'document.pdf');
    const sensitive = await this.encryptSensitive({
      nuip: params.nuip,
      password: params.password
    });
    form.append('nuip', sensitive.nuip);
    form.append('password', sensitive.password);
    if (sensitive.encrypted) {
      form.append('sensitiveEncrypted', 'true');
    }
    form.append('serial', params.serial);
    form.append('serialNumber', params.serialNumber);
    form.append('issuer', params.issuer);
    form.append('originChannel', this.resolveOriginChannel(params.originChannel));
    this.appendOptional(form, 'signReason', params.signReason);
    this.appendOptional(form, 'signLocation', params.signLocation);
    this.appendOptional(form, 'documentType', params.documentType);
    this.appendOptional(form, 'transactionId', params.transactionId);
    this.appendPdfAppearance(form, params);
    return form;
  }

  async buildPdfPfxForm(params: SignPdfPfxMultipartParams): Promise<FormData> {
    const form = new FormData();
    form.append('document', params.document, params.fileName || 'document.pdf');
    form.append('pfx', params.pfx, params.pfxFileName || 'certificate.p12');
    const sensitive = await this.encryptSensitive({ pfxPassword: params.pfxPassword });
    form.append('pfxPassword', sensitive.pfxPassword);
    if (sensitive.encrypted) {
      form.append('sensitiveEncrypted', 'true');
    }
    form.append('originChannel', this.resolveOriginChannel(params.originChannel));
    this.appendOptional(form, 'alias', params.alias);
    this.appendOptional(form, 'signReason', params.signReason);
    this.appendOptional(form, 'signLocation', params.signLocation);
    this.appendOptional(form, 'transactionId', params.transactionId);
    this.appendPdfAppearance(form, params);
    return form;
  }

  async buildCadesTvForm(params: SignCadesTvMultipartParams): Promise<FormData> {
    const form = new FormData();
    form.append('document', params.document, params.fileName || 'document');
    const sensitive = await this.encryptSensitive({
      nuip: params.nuip,
      password: params.password
    });
    form.append('nuip', sensitive.nuip);
    form.append('password', sensitive.password);
    if (sensitive.encrypted) {
      form.append('sensitiveEncrypted', 'true');
    }
    form.append('serial', params.serial);
    form.append('serialNumber', params.serialNumber);
    form.append('issuer', params.issuer);
    form.append('fileName', params.fileName || 'document');
    form.append('originChannel', this.resolveOriginChannel(params.originChannel));
    this.appendOptional(form, 'documentType', params.documentType);
    this.appendOptional(form, 'transactionId', params.transactionId);
    if (params.attached != null) {
      form.append('attached', String(params.attached));
    }
    if (params.timestampEnabled != null) {
      form.append('timestampEnabled', String(params.timestampEnabled));
    }
    return form;
  }

  async buildCadesPfxForm(params: SignCadesPfxMultipartParams): Promise<FormData> {
    const form = new FormData();
    form.append('document', params.document, params.fileName || 'document');
    form.append('pfx', params.pfx, params.pfxFileName || 'certificate.p12');
    const sensitive = await this.encryptSensitive({ pfxPassword: params.pfxPassword });
    form.append('pfxPassword', sensitive.pfxPassword);
    if (sensitive.encrypted) {
      form.append('sensitiveEncrypted', 'true');
    }
    form.append('fileName', params.fileName || 'document');
    form.append('originChannel', this.resolveOriginChannel(params.originChannel));
    this.appendOptional(form, 'alias', params.alias);
    this.appendOptional(form, 'transactionId', params.transactionId);
    if (params.attached != null) {
      form.append('attached', String(params.attached));
    }
    if (params.timestampEnabled != null) {
      form.append('timestampEnabled', String(params.timestampEnabled));
    }
    return form;
  }

  /**
   * Cifra campos sensibles de multipart con el mismo cipher del JSON.
   * El documento binario no se cifra.
   */
  private async encryptSensitive<T extends Record<string, string>>(
    fields: T
  ): Promise<T & { encrypted: boolean }> {
    if (!this.cipher.isEncryptionEnabled) {
      return { ...fields, encrypted: false };
    }
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(fields)) {
      out[key] = value != null && String(value).length > 0
        ? await this.cipher.encrypt(String(value))
        : String(value ?? '');
    }
    return { ...(out as T), encrypted: true };
  }

  private appendPdfAppearance(
    form: FormData,
    params: {
      appearanceEnabled?: boolean;
      mode?: string | null;
      signFieldName?: string | null;
      llx?: number | null;
      lly?: number | null;
      urx?: number | null;
      ury?: number | null;
      page?: number | null;
      contentSignature?: string | null;
      imageBase64?: string | null;
      imageValidation?: boolean | null;
      timestampEnabled?: boolean;
      ltvEnabled?: boolean;
    }
  ): void {
    if (params.appearanceEnabled) {
      form.append('appearanceEnabled', 'true');
      this.appendOptional(form, 'mode', params.mode);
      this.appendOptional(form, 'signFieldName', params.signFieldName);
      if (params.llx != null) form.append('llx', String(params.llx));
      if (params.lly != null) form.append('lly', String(params.lly));
      if (params.urx != null) form.append('urx', String(params.urx));
      if (params.ury != null) form.append('ury', String(params.ury));
      if (params.page != null) form.append('page', String(params.page));
      this.appendOptional(form, 'contentSignature', params.contentSignature);
      this.appendOptional(form, 'imageBase64', params.imageBase64);
      if (params.imageValidation != null) {
        form.append('imageValidation', String(params.imageValidation));
      }
    } else {
      form.append('appearanceEnabled', 'false');
    }
    if (params.timestampEnabled != null) {
      form.append('timestampEnabled', String(params.timestampEnabled));
    }
    if (params.ltvEnabled != null) {
      form.append('ltvEnabled', String(params.ltvEnabled));
    }
  }

  private appendOptional(form: FormData, key: string, value?: string | null): void {
    if (value != null && String(value).trim() !== '') {
      form.append(key, String(value).trim());
    }
  }

  private withOriginChannel<T extends { originChannel?: string }>(
    body: T
  ): T & { originChannel: string } {
    return {
      ...body,
      originChannel: this.resolveOriginChannel(body.originChannel)
    };
  }

  private resolveOriginChannel(value?: string | null): string {
    const fromEnv = (environment as { originChannel?: string }).originChannel;
    const resolved = (value?.trim() || fromEnv?.trim() || 'CERTITOOL WEB');
    return resolved;
  }

  private postMultipartSign(url: string, formData: FormData): Observable<SignMultipartResult> {
    return this.http.post(url, formData, {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map((response: HttpResponse<Blob>) => this.mapMultipartSuccess(response)),
      catchError((error: HttpErrorResponse) => this.unwrapMultipartError(error))
    );
  }

  private mapMultipartSuccess(response: HttpResponse<Blob>): SignMultipartResult {
    return {
      blob: response.body ?? new Blob(),
      fileName: this.parseContentDispositionFileName(response.headers.get('Content-Disposition')),
      transactionId: response.headers.get('X-Sign-Transaction-Id'),
      serial: response.headers.get('X-Sign-Serial'),
      identificationNumber: response.headers.get('X-Sign-Identification-Number'),
      serialNumber: response.headers.get('X-Sign-Serial-Number'),
      documentType: response.headers.get('X-Sign-Document-Type')
    };
  }

  private unwrapMultipartError(error: HttpErrorResponse): Observable<never> {
    const body = error?.error;
    if (body instanceof Blob) {
      return from(body.text()).pipe(
        switchMap((text) => {
          try {
            const json = JSON.parse(text);
            return throwError(() => ({
              ...error,
              error: json
            }));
          } catch {
            return throwError(() => error);
          }
        })
      );
    }
    return throwError(() => error);
  }

  private parseContentDispositionFileName(header: string | null): string | null {
    if (!header) return null;
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
    if (utf8?.[1]) {
      try {
        return decodeURIComponent(utf8[1].trim());
      } catch {
        return utf8[1].trim();
      }
    }
    const plain = /filename="?([^";]+)"?/i.exec(header);
    return plain?.[1]?.trim() || null;
  }

  /**
   * Cifra el body JSON y envía `{ data }` cuando `environment.cipher` está configurado.
   * Si no, envía en claro (entornos de prueba sin cifrado).
   */
  private postEncrypted<T>(url: string, body: unknown): Observable<T> {
    if (!this.cipher.isEncryptionEnabled) {
      return this.http.post<T>(url, body);
    }

    return from(this.cipher.encrypt(JSON.stringify(body))).pipe(
      switchMap((encrypted) =>
        this.http.post<{ data?: string } | T>(url, { data: encrypted })
      ),
      switchMap((response) => this.unwrapCipherResponse<T>(response))
    );
  }

  private unwrapCipherResponse<T>(response: { data?: string } | T): Observable<T> {
    const wrapped = response as { data?: unknown };
    if (wrapped?.data && typeof wrapped.data === 'string') {
      return from(this.cipher.decrypt(wrapped.data)).pipe(
        map((plain) => JSON.parse(plain) as T)
      );
    }
    return of(response as T);
  }
}
